import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Printer, Spinner, Trash, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["new", "quoted", "won", "lost"];
const fmtDate = (d) => d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tone = { new: "bg-emerald-500 text-white", quoted: "bg-amber-100 text-amber-800", won: "bg-zinc-900 text-white", lost: "bg-zinc-200 text-zinc-600" };

export default function AdminQuotes() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/admin/quote-requests").then(({ data }) => { setRows(data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);
  if (user.role !== "super_admin") return <div className="p-8 text-zinc-500">Super admin access only.</div>;

  const setStatus = async (r, status) => {
    try { await api.post(`/admin/quote-requests/${r.id}/status`, { status }); setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, status } : x)); toast.success(`Marked ${status}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete quote request from ${r.company}?`)) return;
    try { await api.delete(`/admin/quote-requests/${r.id}`); setRows((rs) => rs.filter((x) => x.id !== r.id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-quotes-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Label Quote Requests</h1>
        <p className="text-zinc-500 text-sm mt-1">{rows.length} total · {rows.filter((r) => r.status === "new").length} new</p>
      </div>
      <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead>Received</TableHead><TableHead>Company</TableHead><TableHead>Contact</TableHead><TableHead>Service</TableHead>
              <TableHead className="text-right">Labels</TableHead><TableHead>Stock</TableHead><TableHead>Install address</TableHead><TableHead className="text-right">Estimate</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-16"><Spinner size={28} className="animate-spin text-emerald-600 mx-auto" /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-16 text-zinc-400"><Printer size={32} className="mx-auto mb-2 text-zinc-300" />No quote requests yet.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} className={r.status === "lost" ? "opacity-60" : ""} data-testid={`quote-row-${r.id}`}>
                <TableCell className="text-xs text-zinc-500 whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                <TableCell className="text-sm font-medium">{r.company}<div className="text-xs text-zinc-400 font-normal">{r.first_name} {r.last_name}</div></TableCell>
                <TableCell className="text-sm">
                  <a href={`mailto:${r.work_email}`} className="flex items-center gap-1.5 text-emerald-700 hover:underline"><EnvelopeSimple size={14} /> {r.work_email}</a>
                  {r.phone && <div className="text-xs text-zinc-400 mt-0.5">{r.phone}</div>}
                </TableCell>
                <TableCell className="text-sm whitespace-nowrap">{r.service_name}<div className="text-xs text-zinc-400">{r.timeline || "—"}</div></TableCell>
                <TableCell className="text-sm text-right font-semibold">{Number(r.label_count).toLocaleString()}<div className="text-xs text-zinc-400 font-normal">{r.locations} loc.</div></TableCell>
                <TableCell className="text-xs text-zinc-600 max-w-[180px]">{r.stock}</TableCell>
                <TableCell className="text-xs text-zinc-600 max-w-[200px] whitespace-pre-line">{r.install_address || "—"}</TableCell>
                <TableCell className="text-sm text-right font-semibold whitespace-nowrap">{r.estimate_low === r.estimate_high ? money(r.estimate_low) : `${money(r.estimate_low)} – ${money(r.estimate_high)}`}</TableCell>
                <TableCell>
                  <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                    <SelectTrigger className={`h-8 w-[110px] text-xs font-semibold border-0 ${tone[r.status] || ""}`} data-testid={`quote-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell><Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => del(r)} data-testid={`quote-delete-${r.id}`}><Trash size={16} /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
